
import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

export class HeroVideoSlider extends Component {
  constructor() {
    super();
    /** @type {number} */
    this.currentIndex = 0;
    /** @type {number | null} */
    this.autoplayInterval = null;
    /** @type {string[]} */
    this.imagesData = [];
    /** @type {number} */
    this.touchStartX = 0;
    /** @type {number} */
    this.touchEndX = 0;
    /** @type {number} */
    this.touchStartY = 0;
    /** @type {number} */
    this.touchEndY = 0;
    /** @type {number} */
    this.minSwipeDistance = 50;
  }

  connectedCallback() {
    super.connectedCallback();
    
    /** @type {NodeListOf<HTMLElement>} */
    this.slides = this.querySelectorAll('.hero-video-slide');
    
    /** @type {NodeListOf<HTMLButtonElement>} */
    this.dots = this.querySelectorAll('.hero-video-dot');
    
    /** @type {HTMLButtonElement | null} */
    this.prevBtn = this.querySelector('.hero-video-prev');
    
    /** @type {HTMLButtonElement | null} */
    this.nextBtn = this.querySelector('.hero-video-next');

    /** @type {HTMLElement | null} */
    this.hoverMedia = this.querySelector('.hero-video-slider__hover-media');

    this.#initData();
    
    if (!this.slides || this.slides.length === 0) return;

    this.#init();
  }

  #initData() {
    const jsonScript = this.querySelector('script[data-images-json]');
    if (jsonScript) {
      try {
        this.imagesData = JSON.parse(jsonScript.textContent || '[]');
      } catch (e) {
        console.error('HeroVideoSlider: Error parsing images JSON', e);
        this.imagesData = [];
      }
    } else {
      this.imagesData = [];
    }

    console.log('HeroVideoSlider initialized. Slides:', this.slides ? this.slides.length : 0, 'Images:', this.imagesData.length);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopAutoplay();
  }

  #init() {
    // Show the first slide
    this.showSlide(0);

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
      this.prevBtn.addEventListener('mouseenter', () => this.#toggleHoverMedia('prev', true));
      this.prevBtn.addEventListener('mouseleave', () => this.#toggleHoverMedia('prev', false));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
      this.nextBtn.addEventListener('mouseenter', () => this.#toggleHoverMedia('next', true));
      this.nextBtn.addEventListener('mouseleave', () => this.#toggleHoverMedia('next', false));
    }

    if (this.dots) {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.showSlide(index);
        });
      });
    }

    if (this.dataset.autoplay === 'true') {
      this.startAutoplay();
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
    }

    this.addEventListener('mousemove', this.#handleMouseMove);

    // Swipe support
    this.addEventListener('touchstart', (e) => {
      if (e.changedTouches && e.changedTouches[0]) {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchStartY = e.changedTouches[0].screenY;
      }
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      if (e.changedTouches && e.changedTouches[0]) {
        this.touchEndX = e.changedTouches[0].screenX;
        this.touchEndY = e.changedTouches[0].screenY;
        this.#handleSwipe();
      }
    }, { passive: true });
  }

  #handleSwipe() {
    const swipeDistanceX = this.touchEndX - this.touchStartX;
    const swipeDistanceY = this.touchEndY - this.touchStartY;
    
    // Only trigger if horizontal movement is greater than vertical movement
    if (Math.abs(swipeDistanceX) > Math.abs(swipeDistanceY) && Math.abs(swipeDistanceX) > this.minSwipeDistance) {
      if (swipeDistanceX > 0) {
        // Swiped right -> Previous slide
        this.prev();
      } else {
        // Swiped left -> Next slide
        this.next();
      }
      this.stopAutoplay();
      if (this.dataset.autoplay === 'true') {
        this.startAutoplay();
      }
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (isMobileBreakpoint()) return;

    const x = event.clientX;
    const y = event.clientY;

    // Use requestAnimationFrame for smoother movement
    window.requestAnimationFrame(() => {
      if (this.hoverMedia) {
        this.hoverMedia.style.top = `${y}px`;
        this.hoverMedia.style.left = `${x}px`;
      }
    });
  }

  /**
   * @param {number} index
   */
  showSlide(index) {
    if (!this.slides) return;

    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.classList.add('is-active');
        /** @type {HTMLVideoElement | null} */
        const video = slide.querySelector('video');
        if (video && typeof video.play === 'function') {
          video.play().catch(e => console.log('Autoplay prevented:', e));
        }
      } else {
        slide.classList.remove('is-active');
        /** @type {HTMLVideoElement | null} */
        const video = slide.querySelector('video');
        if (video && typeof video.pause === 'function') {
          video.pause();
        }
      }
    });

    if (this.dots) {
      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        
        /** @type {HTMLElement | null} */
        const progress = dot.querySelector('.hero-video-dot-progress');
        if (progress) {
          progress.style.transition = 'none';
          progress.style.width = '0%';
        }
      });
      this.#animateProgress(index);
    }

    this.currentIndex = index;
  }

  /**
   * @param {string} side
   * @param {boolean} visible
   */
  #toggleHoverMedia(side, visible) {
    if (isMobileBreakpoint()) return;
    if (!this.hoverMedia) return;

    if (visible) {
      this.#updateHoverMediaContent(side);
      this.hoverMedia.classList.add('is-visible');
    } else {
      this.hoverMedia.classList.remove('is-visible');
    }
  }

  /**
   * @param {string} side
   */
  #updateHoverMediaContent(side) {
    if (!this.hoverMedia || !this.imagesData || this.imagesData.length < 2) return;
    if (!this.slides) return;

    const currentIndex = this.currentIndex;
    let targetIndex;

    if (side === 'prev') {
      targetIndex = (currentIndex - 1 + this.slides.length) % this.slides.length;
    } else {
      targetIndex = (currentIndex + 1) % this.slides.length;
    }

    const imgUrl = this.imagesData[targetIndex];
    if (imgUrl) {
      const existingImg = this.hoverMedia.querySelector('img');
      if (!existingImg || existingImg.src !== imgUrl) {
        this.hoverMedia.innerHTML = `<img src="${imgUrl}" alt="${side}" style="width:100%;height:100%;object-fit:cover;">`;
      }
    }
  }

  next() {
    const slides = this.slides;
    if (!slides) return;
    let nextIndex = this.currentIndex + 1;
    if (nextIndex >= slides.length) {
      nextIndex = 0;
    }
    this.showSlide(nextIndex);
  }

  prev() {
    const slides = this.slides;
    if (!slides) return;
    let prevIndex = this.currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = slides.length - 1;
    }
    this.showSlide(prevIndex);
  }

  startAutoplay() {
    const speed = parseInt(this.dataset.speed || '6000');
    if (speed > 0) {
      this.stopAutoplay();
      this.autoplayInterval = window.setInterval(() => this.next(), speed);
      
      // Restart current dot animation
      this.#animateProgress(this.currentIndex);
    }
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
    // Remove transitions and progress from all dots
    if (this.dots) {
      this.dots.forEach(dot => {
        /** @type {HTMLElement | null} */
        const progress = dot.querySelector('.hero-video-dot-progress');
        if (progress) {
          progress.style.transition = 'none';
          progress.style.width = '0%';
        }
      });
    }
  }

  /**
   * @param {number} index
   */
  #animateProgress(index) {
    if (!this.dots || this.dataset.autoplay !== 'true') return;
    const dot = this.dots[index];
    if (!dot) return;
    /** @type {HTMLElement | null} */
    const progress = dot.querySelector('.hero-video-dot-progress');
    if (!progress) return;

    progress.style.transition = 'none';
    progress.style.width = '0%';
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const speed = parseInt(this.dataset.speed || '6000');
        if (progress) {
          progress.style.transition = `width ${speed}ms linear`;
          progress.style.width = '100%';
        }
      });
    });
  }
}

if (!customElements.get('hero-video-slider')) {
  customElements.define('hero-video-slider', HeroVideoSlider);
}
